import { Label } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { LayoutSettingSelectedEventData } from '@databases/analytics/src/types';
import Select from '@databases/atlaskit/Select';
import { DatabaseCardWidth, DatabaseViewSettings } from '@databases/types/database/view';
import { ViewChangeType, ViewChangeTypes } from '@databases/utils/database/view/view';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../i18n';

interface Option {
  label: string;
  value: DatabaseCardWidth;
}

export interface CardWidthSettingProps {
  value: DatabaseViewSettings['layout'];
  onChange: (value: ViewChangeType) => void;
  source: LayoutSettingSelectedEventData['source'];
  contentId: string;
}

const CardWidthSetting: FunctionComponent<CardWidthSettingProps> = ({
  value,
  onChange,
  source,
  contentId,
}) => {
  const { formatMessage } = useIntl();
  const client = useAnalytics();

  const options = useMemo(
    () => [
      {
        label: formatMessage(messages['view-settings.layout.card-width.small-text']),
        value: DatabaseCardWidth.SMALL,
      },
      {
        label: formatMessage(messages['view-settings.layout.card-width.medium-text']),
        value: DatabaseCardWidth.MEDIUM,
      },
      {
        label: formatMessage(messages['view-settings.layout.card-width.large-text']),
        value: DatabaseCardWidth.LARGE,
      },
    ],
    [formatMessage]
  );

  const resolvedValue = useMemo(
    () => options.find((option) => option.value === value.cardWidth) ?? null,
    [options, value.cardWidth]
  );

  const handleChange = useCallback(
    async (option: Option | null) => {
      if (!option) {
        return;
      }
      onChange({
        type: ViewChangeTypes.LAYOUT_CARD_WIDTH,
        value: option.value as DatabaseCardWidth,
      });

      await client.sendLayoutSettingSelectedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source,
        layoutSetting: 'cardWidth',
        value: option.value,
      });
    },
    [client, contentId, onChange, source]
  );

  return (
    <Box>
      <Label htmlFor="view-settings-layout-card-width-select">
        {formatMessage(messages['view-settings.layout.card-width.label'])}
      </Label>
      <Select<Option>
        value={resolvedValue}
        options={options}
        onChange={handleChange}
        inputId="view-settings-layout-card-width-select"
      />
    </Box>
  );
};

export default CardWidthSetting;
